import { useMemo } from 'react';
import { FriendLocation, TemporaryLocation, TemporaryLocationWithFriend, User } from '../../../types';
import { clusterFriends, isTempLocationCurrent, isTempLocationUpcoming } from '../utils';
import { MapFilters } from './useMapFilters';

interface UseFriendClustersParams {
  locations: FriendLocation[];
  tempLocations: TemporaryLocationWithFriend[];
  myTempLocations: TemporaryLocation[];
  mapZoom: number;
  mapFilters: MapFilters;
  user: User | null;
  listMemberIds?: number[]; // IDs of friends in the selected list (for list filtering)
  excludeFromClusteringIds?: number[]; // IDs to exclude from clustering (e.g., route destination)
}

/**
 * Hook to cluster friend markers based on zoom level and filters
 */
export const useFriendClusters = ({
  locations,
  tempLocations,
  myTempLocations,
  mapZoom,
  mapFilters,
  user,
  listMemberIds = [],
  excludeFromClusteringIds = [],
}: UseFriendClustersParams) => {
  return useMemo(() => {
    const allMarkersToCluster: FriendLocation[] = [];

    // Add friends' permanent addresses if enabled
    if (mapFilters.showPermanentAddresses) {
      let permanentLocations = locations
        .filter(
          (friend) =>
            friend.latitude != null &&
            friend.longitude != null &&
            !isNaN(friend.latitude) &&
            !isNaN(friend.longitude)
        )
        .map(friend => ({
          ...friend,
          markerType: 'official' as const,
        }));

      // Filter by selected friend if active
      if (mapFilters.selectedFriendId !== null) {
        permanentLocations = permanentLocations.filter(
          friend => friend.id === mapFilters.selectedFriendId
        );
      }

      // Filter by selected list if active
      if (mapFilters.selectedListId !== null && listMemberIds.length > 0) {
        permanentLocations = permanentLocations.filter(
          friend => listMemberIds.includes(friend.id)
        );
      }

      allMarkersToCluster.push(...permanentLocations);
    }

    // Add user's permanent address if enabled and exists (only if no friend/list filter is active)
    if (mapFilters.showMyTempLocations &&
        user?.address?.latitude &&
        user?.address?.longitude &&
        mapFilters.selectedFriendId === null &&
        mapFilters.selectedListId === null) {
      const userAddress: FriendLocation = {
        id: user.id,
        first_name: user.first_name,
        last_name: user.last_name,
        profile_photo: user.profile_photo,
        latitude: user.address.latitude,
        longitude: user.address.longitude,
        city: user.address.city || '',
        country: user.address.country || '',
        markerType: 'myPermanent' as const, // Identify as user's permanent address
        isMyLocation: true, // Flag to identify as my location
      };
      allMarkersToCluster.push(userAddress);
    }

    // Add friends' temporary locations if enabled
    let filteredFriendsTempLocations = tempLocations.filter((tempLoc) => {
      const isCurrent = isTempLocationCurrent(tempLoc);
      const isUpcoming = isTempLocationUpcoming(tempLoc);

      // Apply the same filters as the marker rendering
      if (isCurrent && !mapFilters.showCurrentTempLocations) return false;
      if (isUpcoming && !mapFilters.showUpcomingTempLocations) return false;
      if (!isCurrent && !isUpcoming) return false; // Past locations not shown

      return tempLoc.latitude != null &&
             tempLoc.longitude != null &&
             !isNaN(tempLoc.latitude) &&
             !isNaN(tempLoc.longitude);
    });

    // Filter by selected friend if active
    if (mapFilters.selectedFriendId !== null) {
      filteredFriendsTempLocations = filteredFriendsTempLocations.filter(
        tempLoc => tempLoc.friend_id === mapFilters.selectedFriendId
      );
    }

    // Filter by selected list if active
    if (mapFilters.selectedListId !== null && listMemberIds.length > 0) {
      filteredFriendsTempLocations = filteredFriendsTempLocations.filter(
        tempLoc => listMemberIds.includes(tempLoc.friend_id)
      );
    }

    // Convert friends' temp locations to FriendLocation format for clustering
    // Use temp location ID (with high offset) to avoid collision with friend IDs
    const friendsTempLocationsAsFriends: FriendLocation[] = filteredFriendsTempLocations.map(tempLoc => ({
      id: tempLoc.id + 1000000, // Add large offset to ensure unique ID
      first_name: tempLoc.first_name || '',
      last_name: tempLoc.last_name || '',
      profile_photo: tempLoc.profile_photo,
      latitude: tempLoc.latitude,
      longitude: tempLoc.longitude,
      city: '',
      country: '',
      markerType: 'temporary' as const,
      tempLocationType: tempLoc.type,
      tempLocationName: tempLoc.name,
      tempLocationDescription: tempLoc.description,
      tempLocationStartDate: tempLoc.start_datetime,
      tempLocationEndDate: tempLoc.end_datetime,
      tempLocationIsActive: tempLoc.is_active,
      friendId: tempLoc.friend_id, // Store real friend ID for contact button
    } as any));

    allMarkersToCluster.push(...friendsTempLocationsAsFriends);

    // Add my temp locations to clustering if enabled (and no friend/list filter is active)
    if (mapFilters.showMyTempLocations && user && !mapFilters.selectedFriendId && !mapFilters.selectedListId) {
      const myTempLocationsAsFriends: FriendLocation[] = myTempLocations.map(myTempLoc => ({
        id: myTempLoc.id + 2000000, // Different offset to avoid collision with friends' temp locations
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        profile_photo: user.profile_photo,
        latitude: myTempLoc.latitude,
        longitude: myTempLoc.longitude,
        city: '',
        country: '',
        markerType: 'myTemporary' as const,
        tempLocationType: myTempLoc.type,
        tempLocationName: myTempLoc.name,
        tempLocationDescription: myTempLoc.description,
        tempLocationStartDate: myTempLoc.start_datetime,
        tempLocationEndDate: myTempLoc.end_datetime,
        tempLocationIsActive: myTempLoc.is_active,
        tempLocationId: myTempLoc.id, // Store real temp location ID for editing
        isMyLocation: true, // Flag to identify as my location
      } as any));
      allMarkersToCluster.push(...myTempLocationsAsFriends);
    }

    // Filter out markers that should not be clustered (e.g., route origin, destination, waypoints)
    let excludedMarkers: FriendLocation[] = [];
    let markersToCluster = allMarkersToCluster;

    if (excludeFromClusteringIds.length > 0) {
      excludedMarkers = allMarkersToCluster.filter(marker => {
        const markerId = marker.id;
        const markerFriendId = (marker as any).friendId;
        return excludeFromClusteringIds.includes(markerId) ||
               (markerFriendId && excludeFromClusteringIds.includes(markerFriendId));
      });

      markersToCluster = allMarkersToCluster.filter(marker => {
        const markerId = marker.id;
        const markerFriendId = (marker as any).friendId;
        return !excludeFromClusteringIds.includes(markerId) &&
               (!markerFriendId || !excludeFromClusteringIds.includes(markerFriendId));
      });
    }

    // Cluster radius: 30px - smaller value = less clustering (markers must be closer to group)
    const clusters = clusterFriends(markersToCluster, mapZoom, 30);

    // Return both clusters and excluded markers
    return { clusters, excludedMarkers };
  }, [
    locations,
    tempLocations,
    myTempLocations,
    mapZoom,
    mapFilters.showPermanentAddresses,
    mapFilters.showCurrentTempLocations,
    mapFilters.showUpcomingTempLocations,
    mapFilters.showMyTempLocations,
    mapFilters.selectedFriendId,
    mapFilters.selectedListId,
    listMemberIds,
    excludeFromClusteringIds,
    user
  ]);
};
